import type { Sim } from '@/types'

import { palette } from '@/constants/palette'

const COUNT = 70
const BINS = 18
const RESET_AFTER = 16
const SUBSTEPS = 2

const lerpColor = (from: number[], to: number[], t: number) => {
    const clamped = Math.min(1, Math.max(0, t))

    return `rgb(${Math.round(from[0] + (to[0] - from[0]) * clamped)}, ${Math.round(from[1] + (to[1] - from[1]) * clamped)}, ${Math.round(from[2] + (to[2] - from[2]) * clamped)})`
}

const SLOW = [154, 160, 174]
const MEDIUM = [47, 73, 255]
const FAST = [255, 91, 46]

const roundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
    if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, width, height, radius)
    else ctx.rect(x, y, width, height)
}

export const createGas = (): Sim => {
    const x = new Float32Array(COUNT)
    const y = new Float32Array(COUNT)
    const vx = new Float32Array(COUNT)
    const vy = new Float32Array(COUNT)
    const bins = new Float32Array(BINS)
    const counts = new Float32Array(BINS)
    let box = { x: 0, y: 0, width: 0, height: 0 }
    let chart = { x: 0, y: 0, width: 0, height: 0 }
    let radius = 3
    let speed = 60
    let initialised = false
    let sinceReset = 0

    const reset = () => {
        for (let index = 0; index < COUNT; index++) {
            let attempts = 0

            do {
                x[index] = box.x + radius + Math.random() * (box.width - radius * 2)
                y[index] = box.y + radius + Math.random() * (box.height - radius * 2)
                attempts++
            } while (attempts < 30 && overlapsEarlier(index))

            const angle = Math.random() * Math.PI * 2

            vx[index] = Math.cos(angle) * speed
            vy[index] = Math.sin(angle) * speed
        }

        bins.fill(0)
        sinceReset = 0
        initialised = true
    }

    const overlapsEarlier = (index: number) => {
        for (let other = 0; other < index; other++) {
            const dx = x[other] - x[index]
            const dy = y[other] - y[index]

            if (dx * dx + dy * dy < (radius * 2.2) ** 2) return true
        }

        return false
    }

    const step = (dt: number) => {
        const left = box.x + radius
        const right = box.x + box.width - radius
        const top = box.y + radius
        const bottom = box.y + box.height - radius

        for (let index = 0; index < COUNT; index++) {
            x[index] += vx[index] * dt
            y[index] += vy[index] * dt

            if (x[index] < left) {
                x[index] = left
                vx[index] = Math.abs(vx[index])
            }
            else if (x[index] > right) {
                x[index] = right
                vx[index] = -Math.abs(vx[index])
            }

            if (y[index] < top) {
                y[index] = top
                vy[index] = Math.abs(vy[index])
            }
            else if (y[index] > bottom) {
                y[index] = bottom
                vy[index] = -Math.abs(vy[index])
            }
        }

        const reach = radius * 2

        for (let first = 0; first < COUNT; first++) {
            for (let second = first + 1; second < COUNT; second++) {
                const dx = x[second] - x[first]
                const dy = y[second] - y[first]
                const distanceSquared = dx * dx + dy * dy

                if (distanceSquared >= reach * reach) continue

                const distance = Math.sqrt(distanceSquared) || 0.001
                const nx = dx / distance
                const ny = dy / distance
                const push = (reach - distance) / 2

                x[first] -= nx * push
                y[first] -= ny * push
                x[second] += nx * push
                y[second] += ny * push

                const approach = (vx[first] - vx[second]) * nx + (vy[first] - vy[second]) * ny

                if (approach <= 0) continue

                vx[first] -= approach * nx
                vy[first] -= approach * ny
                vx[second] += approach * nx
                vy[second] += approach * ny
            }
        }
    }

    return {
        resize: (width: number, height: number) => {
            const previous = box
            const previousSpeed = speed

            box = { x: 14, y: 14, width: width - 28, height: height * 0.6 - 14 }
            chart = { x: 22, y: height * 0.66, width: width - 44, height: height * 0.34 - 26 }
            radius = Math.max(2.4, Math.min(4, box.width / 110))
            speed = Math.max(40, box.width * 0.24)

            if (!initialised) {
                reset()
                return
            }

            const speedScale = speed / previousSpeed

            for (let index = 0; index < COUNT; index++) {
                x[index] = box.x + ((x[index] - previous.x) / Math.max(previous.width, 1)) * box.width
                y[index] = box.y + ((y[index] - previous.y) / Math.max(previous.height, 1)) * box.height
                vx[index] *= speedScale
                vy[index] *= speedScale
            }
        },

        update: (dt: number) => {
            if (!initialised) return

            sinceReset += dt

            if (sinceReset > RESET_AFTER) reset()

            for (let index = 0; index < SUBSTEPS; index++) step(dt / SUBSTEPS)

            counts.fill(0)

            const maxSpeed = speed * 3

            for (let index = 0; index < COUNT; index++) {
                const value = Math.sqrt(vx[index] * vx[index] + vy[index] * vy[index])
                const bin = Math.min(BINS - 1, Math.floor((value / maxSpeed) * BINS))

                counts[bin]++
            }

            const blend = 1 - Math.exp(-dt * 4)

            for (let index = 0; index < BINS; index++) bins[index] += (counts[index] - bins[index]) * blend
        },

        draw: (ctx: CanvasRenderingContext2D) => {
            if (!initialised) return

            ctx.save()

            ctx.strokeStyle = palette.faint
            ctx.lineWidth = 1
            ctx.beginPath()
            roundedRect(ctx, box.x, box.y, box.width, box.height, 14)
            ctx.stroke()

            for (let index = 0; index < COUNT; index++) {
                const value = Math.sqrt(vx[index] * vx[index] + vy[index] * vy[index]) / (speed * 2)

                ctx.fillStyle = value < 0.5 ? lerpColor(SLOW, MEDIUM, value * 2) : lerpColor(MEDIUM, FAST, (value - 0.5) * 2)
                ctx.beginPath()
                ctx.arc(x[index], y[index], radius, 0, Math.PI * 2)
                ctx.fill()
            }

            const sigma = speed / Math.SQRT2
            const binWidth = (speed * 3) / BINS
            const expectedPeak = COUNT * (binWidth / sigma) * Math.exp(-0.5)
            const unit = chart.height / (expectedPeak * 1.8)
            const barWidth = chart.width / BINS

            ctx.fillStyle = '#e3e6ee'

            for (let index = 0; index < BINS; index++) {
                const barHeight = Math.min(chart.height, bins[index] * unit)

                if (barHeight < 0.5) continue

                ctx.beginPath()
                roundedRect(ctx, chart.x + index * barWidth + 1.5, chart.y + chart.height - barHeight, Math.max(1, barWidth - 3), barHeight, 3)
                ctx.fill()
            }

            ctx.strokeStyle = palette.brand
            ctx.lineWidth = 1.6
            ctx.beginPath()

            for (let sample = 0; sample <= 60; sample++) {
                const velocity = (sample / 60) * speed * 3
                const density = (velocity / (sigma * sigma)) * Math.exp(-(velocity * velocity) / (2 * sigma * sigma))
                const expected = COUNT * density * binWidth
                const px = chart.x + (velocity / (speed * 3)) * chart.width
                const py = chart.y + chart.height - Math.min(chart.height, expected * unit)

                if (sample === 0) ctx.moveTo(px, py)
                else ctx.lineTo(px, py)
            }

            ctx.stroke()

            ctx.strokeStyle = palette.faint
            ctx.lineWidth = 1
            ctx.beginPath()
            ctx.moveTo(chart.x, chart.y + chart.height + 0.5)
            ctx.lineTo(chart.x + chart.width, chart.y + chart.height + 0.5)
            ctx.stroke()

            ctx.font = '500 10px ui-monospace, SFMono-Regular, Menlo, monospace'
            ctx.fillStyle = palette.muted
            ctx.textAlign = 'left'
            ctx.fillText('speed', chart.x, chart.y + chart.height + 14)
            ctx.textAlign = 'right'
            ctx.fillStyle = palette.brand
            ctx.fillText('Maxwell-Boltzmann', chart.x + chart.width, chart.y + 4)

            ctx.restore()
        }
    }
}
